<template>
  <f7-page class="home-page">
    <template #fixed>
      <MainHeaderComponent type="back" />
    </template>

    <div class="bg-gray title-container">
      <div class="title">
        <font-awesome-icon :icon="['fad', 'cog']" fixed-width />

        <div class="text">
          <h1>{{ $t.getTranslation('LBL_COUNTRY_SAVE') }}</h1>
        </div>
      </div>

      <div class="button-container"></div>
    </div>

    <section class="form-container">
      <div class="container">
        <f7-list no-hairlines>
          <f7-list-item divider>{{ $t.getTranslation('LBL_COUNTRY_DETAILS') }}</f7-list-item>
          <f7-list-input
            v-model:value="formData.CountryCode"
            name="CountryCode"
            :required="validationRules?.CountryCode?.required"
            :minlength="validationRules?.CountryCode?.minimumLength"
            :maxlength="validationRules?.CountryCode?.maximumLength"
            :label="$t.getTranslation('LBL_COUNTRY_CODE')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_REQUIRED')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="text"
            validate
          >
          </f7-list-input>

          <f7-list-input
            v-model:value="formData.CountryName['EN']"
            name="CountryName"
            :required="validationRules?.CountryName?.required"
            :minlength="validationRules?.CountryName?.minimumLength"
            :maxlength="validationRules?.CountryName?.maximumLength"
            :label="$t.getTranslation('LBL_COUNTRY_NAME_EN')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_REQUIRED')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="text"
            validate
          >
          </f7-list-input>

          <f7-list-input
            v-model:value="formData.CountryName['CHS']"
            name="CountryName"
            :required="validationRules?.CountryName?.required"
            :minlength="validationRules?.CountryName?.minimumLength"
            :maxlength="validationRules?.CountryName?.maximumLength"
            :label="$t.getTranslation('LBL_COUNTRY_NAME_CHS')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_REQUIRED')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="text"
            validate
          >
          </f7-list-input>

          <f7-list-input
            v-model:value="formData.IsTopCountry"
            name="IsTopCountry"
            :required="validationRules?.IsTopCountry?.required"
            :minlength="validationRules?.IsTopCountry?.minimumLength"
            :maxlength="validationRules?.IsTopCountry?.maximumLength"
            :label="$t.getTranslation('LBL_IS_TOP_COUNTRY')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_OPTIONAL')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="select"
            validate
          >
            <option v-for="status in statusList" :key="'st_' + status.Code" :value="status.Code">{{ status.Name }}</option>
          </f7-list-input>

          <f7-list-input
            v-model:value="formData.StatusCode"
            name="StatusCode"
            :required="validationRules?.StatusCode?.required"
            :minlength="validationRules?.StatusCode?.minimumLength"
            :maxlength="validationRules?.StatusCode?.maximumLength"
            :label="$t.getTranslation('LBL_STATUS')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_OPTIONAL')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="select"
            validate
          >
            <option v-for="status in countryStatusList" :key="'st_' + status.Code" :value="status.Code">{{ status.Name }}</option>
          </f7-list-input>

          <f7-button large fill raised preloader @click="save" :disabled="(formData.CountryDigit == '' || formData.CountryDigit == null) && formData.IsTopCountry > 0">
            {{ $t.getTranslation('LBL_SAVE') }}
          </f7-button>
          <f7-button large fill raised preloader @click="back">
            {{ $t.getTranslation('LBL_BACK') }}
          </f7-button>
        </f7-list>
      </div>
    </section>

    <MainFooterComponent />
  </f7-page>
</template>

<script>
import { defineComponent, ref, computed, reactive, onMounted, inject } from 'vue'

import { useStore } from '@/store'

import { $HTTP } from '@/utils/axios'
import { helpers } from '@/utils/helpers.js'

import { validations } from '@/utils/validations'
import validationRulesConfig from '@/validationRules'

import MainHeaderComponent from '@/components/MainHeaderComponent.vue'
import MainFooterComponent from '@/components/MainFooterComponent.vue'

export default defineComponent({
  name: 'CustomerSavePage',
  components: {
    MainHeaderComponent,
    MainFooterComponent
  },
  props: { f7router: Object, f7route: Object },
  setup(props) {
    const $t = inject('$translation')
    const store = useStore()
    const isUpdate = ref(false)
    const isButtonProcessing = ref(false)
    const currentLanguageCode = computed(() => store.getters['translation/getLanguage'])
    const countryStatusList = computed(() => store.getters['config/getData']?.statusList)
    const uploadedImages = ref([])
    const countryId = ref(null)
    const formData = reactive({
      CountryCode: '',
      CountryDigit: '',
      IsTopCountry: '',
      CountryName: {
        EN: '',
        CHS: ''
      }
    })

    const statusList = ref([
      {
        Code: 1,
        Name: $t.getTranslation('LBL_YES')
      },
      {
        Code: 0,
        Name: $t.getTranslation('LBL_NO')
      }
    ])

    let validationRules = {}
    for (let keys in formData) {
      if (validationRulesConfig && validationRulesConfig[keys]) {
        validationRules[keys] = validationRulesConfig[keys]
      }
    }

    onMounted(async () => {
      if (props?.f7route?.query && props?.f7route?.query?.CountryId) {
        isUpdate.value = true
        countryId.value = props?.f7route?.query?.CountryId
        await getData()
      }
    })

    const getData = async () => {
      try {
        helpers.showLoader()
        let res = await $HTTP.get(`/admin/country/view?CountryId=${countryId.value}&LanguageCode=${currentLanguageCode.value || 'EN'}`)
        if (res?.status === 200 && res?.data?.data) {
          helpers.hideLoader()
          for (var key in res?.data?.data) {
            formData[key] = res?.data?.data[key]
          }

          helpers.hideLoader()
          return
        }
        throw new Error($t.getTranslation('ERR_SERVER_NOT_RESPONDING'))
      } catch (err) {
        helpers.hideLoader()
        helpers.catchError(err, true)
      }
    }

    const validate = (showNotifications) => {
      const isValid = validations.validate({
        validateData: formData,
        validationRules: validationRules,
        showNotifications: showNotifications
      })

      return isValid
    }

    const isDisableSaveButton = computed(() => {
      let isValid = validate(false)

      return !isValid
    })

    const save = async () => {
      try {
        helpers.showLoader()
        let res = await $HTTP.post(`/admin/country/status`, formData)
        if (res?.status === 200 && res?.data?.data) {
          helpers.createNotification({ type: 'success', title: $t.getTranslation('LBL_SUCCESS'), message: $t.getTranslation('LBL_INFO_SUCCESSFULLY_SAVE_COUNTRY_INFO') })
          helpers.hideLoader()
          return
        }
        throw new Error($t.getTranslation('ERR_SERVER_NOT_RESPONDING'))
      } catch (err) {
        helpers.hideLoader()
        helpers.catchError(err, true)
      }
    }

    // const upload = (type) => {
    //   props.f7router.navigate({ name: "categoryUpload", params: {id: categoryId.value}, query: {type: type } });
    // }

    const back = () => {
      if (props?.f7route?.query?.isFeatured) {
        props.f7router.navigate({ name: 'countryPositionList', params: {}, query: {} })
        return
      }
      props.f7router.navigate({ name: 'countryList', params: {}, query: {} })
    }

    return { isUpdate, formData, validationRules, isButtonProcessing, isDisableSaveButton, uploadedImages, back, statusList, save, countryStatusList }
  }
})
</script>
